import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const GameContext = createContext();

const STORAGE_KEYS = {
  stars: 'lioncubai_stars',
  badges: 'lioncubai_badges',
  age: 'lioncubai_age',
  highScores: 'lioncubai_highscores',
  progress: 'lioncubai_progress',
  premium: 'lioncubai_premium',
  booksRead: 'lioncubai_booksread',
  sound: 'lioncubai_sound',
  childName: 'lioncubai_childname',
  streak: 'lioncubai_streak',
};

const BADGE_DEFS = [
  { id: 'first-steps', name: 'First Steps', icon: '🌟', desc: 'Complete any first activity', condition: (p) => p.totalActivities >= 1 },
  { id: 'game-on', name: 'Game On!', icon: '🎮', desc: 'Play first game in arcade', condition: (p) => p.gamesPlayed >= 1 },
  { id: 'story-lover', name: 'Story Lover', icon: '📖', desc: 'Generate 3 stories', condition: (p) => p.storiesGenerated >= 3 },
  { id: 'little-artist', name: 'Little Artist', icon: '🎨', desc: 'Save 3 colored/doodled pages', condition: (p) => p.artSaved >= 3 },
  { id: 'math-whiz', name: 'Math Whiz', icon: '🔢', desc: 'Score 5/5 in Number Dash', condition: (p) => p.perfectMath >= 1 },
  { id: 'speed-runner', name: 'Speed Runner', icon: '🏃', desc: 'Complete Number Dash under 60s', condition: (p) => p.fastMath >= 1 },
  { id: 'memory-king', name: 'Memory King', icon: '🧠', desc: 'Complete Memory Match Hard', condition: (p) => p.memoryHard >= 1 },
  { id: 'simon-says', name: 'Simon Master', icon: '🎵', desc: 'Reach round 10 in Brain Blocks', condition: (p) => p.brainRound >= 10 },
  { id: 'master-explorer', name: 'Master Explorer', icon: '🗺️', desc: 'Find all 3 endings in Adventure', condition: (p) => p.adventureEndings >= 3 },
  { id: 'escape-artist', name: 'Escape Artist', icon: '🔐', desc: 'Complete Jungle Escape Hard', condition: (p) => p.escapeHard >= 1 },
  { id: 'wildlife-champion', name: 'Wildlife Champion', icon: '🦁', desc: 'Score 100% in Animal Quiz', condition: (p) => p.perfectQuiz >= 1 },
  { id: 'habitat-hero', name: 'Habitat Hero', icon: '🌿', desc: 'Sort 100 animals correctly', condition: (p) => p.animalsSorted >= 100 },
  { id: 'wordsmith', name: 'Wordsmith', icon: '🎭', desc: 'Spell 20 words in Word Safari', condition: (p) => p.wordsSpelled >= 20 },
  { id: 'colour-master', name: 'Colour Master', icon: '🎨', desc: 'Complete all Color Blast mini-games', condition: (p) => p.colorBlastComplete >= 3 },
  { id: 'doodle-king', name: 'Doodle King', icon: '✏️', desc: 'Save 10 doodles', condition: (p) => p.doodlesSaved >= 10 },
  { id: 'world-explorer', name: 'World Explorer', icon: '🌍', desc: 'Explore all 6 continents', condition: (p) => p.continentsExplored >= 6 },
  { id: 'star-collector', name: 'Star Collector', icon: '⭐', desc: 'Earn 50 total stars', condition: (p) => p.totalStars >= 50 },
  { id: 'leos-best-friend', name: "Leo's Best Friend", icon: '🦁', desc: 'Earn 100 total stars', condition: (p) => p.totalStars >= 100 },
  { id: 'bookworm', name: 'Bookworm', icon: '📚', desc: 'Finish reading 3 storybooks', condition: (p) => p.booksFinished >= 3 },
];

const DEFAULT_PROGRESS = {
  totalActivities: 0, gamesPlayed: 0, storiesGenerated: 0, artSaved: 0,
  perfectMath: 0, fastMath: 0, memoryHard: 0, brainRound: 0,
  adventureEndings: 0, escapeHard: 0, perfectQuiz: 0, animalsSorted: 0,
  wordsSpelled: 0, colorBlastComplete: 0, doodlesSaved: 0,
  continentsExplored: 0, totalStars: 0, songsCompleted: 0, booksFinished: 0,
};

export function GameProvider({ children }) {
  const [stars, setStars] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.stars);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [badges, setBadges] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.badges);
    return saved ? JSON.parse(saved) : [];
  });

  const [ageGroup, setAgeGroup] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.age) || 'young';
  });

  const [progress, setProgress] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.progress);
    return saved ? { ...DEFAULT_PROGRESS, ...JSON.parse(saved) } : { ...DEFAULT_PROGRESS };
  });

  const [highScores, setHighScores] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.highScores);
    return saved ? JSON.parse(saved) : {};
  });

  // ── NEW: premium subscription state ──
  const [isPremium, setIsPremium] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.premium) === 'true';
  });

  // ── NEW: track which books have been finished ──
  const [booksRead, setBooksRead] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.booksRead);
    return saved ? JSON.parse(saved) : [];
  });

  // ── Sound on/off (default on) ──
  const [soundOn, setSoundOn] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.sound) !== 'false';
  });

  // ── Child's name for personalized greetings ──
  const [childName, setChildNameState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.childName) || '';
  });

  // ── Daily visit streak ──
  const [streak, setStreak] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.streak);
    return saved ? JSON.parse(saved) : { count: 0, lastDate: null };
  });

  // Update streak once per day on load
  useEffect(() => {
    const today = new Date().toDateString();
    setStreak(prev => {
      if (prev.lastDate === today) return prev;
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const count = prev.lastDate === yesterday ? prev.count + 1 : 1;
      return { count, lastDate: today };
    });
  }, []);

  const [leoMood, setLeoMood] = useState('happy');
  const [showCelebration, setShowCelebration] = useState(false);
  const [newBadge, setNewBadge] = useState(null);

  // Persist
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.stars, stars); }, [stars]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.badges, JSON.stringify(badges)); }, [badges]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.age, ageGroup); }, [ageGroup]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.progress, JSON.stringify(progress)); }, [progress]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.highScores, JSON.stringify(highScores)); }, [highScores]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.premium, isPremium); }, [isPremium]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.booksRead, JSON.stringify(booksRead)); }, [booksRead]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.sound, soundOn); }, [soundOn]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.streak, JSON.stringify(streak)); }, [streak]);

  const addStars = useCallback((count) => {
    setStars(prev => {
      const newTotal = prev + count;
      const oldTens = Math.floor(prev / 10);
      const newTens = Math.floor(newTotal / 10);
      if (newTens > oldTens) {
        setTimeout(() => setShowCelebration(true), 300);
      }
      return newTotal;
    });
    setProgress(prev => ({ ...prev, totalStars: (prev.totalStars || 0) + count }));
  }, []);

  const updateProgress = useCallback((key, value) => {
    setProgress(prev => {
      const updated = { ...prev, [key]: typeof value === 'function' ? value(prev[key]) : value };
      BADGE_DEFS.forEach(badge => {
        if (!badges.includes(badge.id) && badge.condition(updated)) {
          setBadges(b => [...b, badge.id]);
          setNewBadge(badge);
          setTimeout(() => setNewBadge(null), 4000);
        }
      });
      return updated;
    });
  }, [badges]);

  const updateHighScore = useCallback((gameId, score) => {
    setHighScores(prev => {
      const current = prev[gameId] || 0;
      if (score > current) return { ...prev, [gameId]: score };
      return prev;
    });
  }, []);

  // ── NEW: mark a book finished (awards a badge after 3) ──
  const markBookRead = useCallback((bookId) => {
    setBooksRead(prev => {
      if (prev.includes(bookId)) return prev;
      const updated = [...prev, bookId];
      updateProgress('booksFinished', updated.length);
      return updated;
    });
  }, [updateProgress]);

  // ── NEW: unlock / lock premium ──
  const unlockPremium = useCallback(() => setIsPremium(true), []);
  const cancelPremium = useCallback(() => setIsPremium(false), []);

  const toggleSound = useCallback(() => setSoundOn(s => !s), []);

  const setChildName = useCallback((name) => {
    const clean = String(name || '').trim().slice(0, 20);
    setChildNameState(clean);
    localStorage.setItem(STORAGE_KEYS.childName, clean);
  }, []);

  const value = {
    stars, addStars,
    soundOn, toggleSound,
    childName, setChildName,
    streak,
    badges, BADGE_DEFS,
    ageGroup, setAgeGroup,
    progress, updateProgress,
    highScores, updateHighScore,
    leoMood, setLeoMood,
    showCelebration, setShowCelebration,
    newBadge, setNewBadge,
    // new exports
    isPremium, unlockPremium, cancelPremium,
    booksRead, markBookRead,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within GameProvider');
  return ctx;
}

export default GameContext;
